import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { sankey, sankeyLinkHorizontal, type SankeyGraph } from 'd3-sankey';
import type { KeptMechanism, PremiumFlow, PremiumFlowFate } from '@steward/shared';

const FATES: { key: PremiumFlowFate; label: string; color: string }[] = [
  { key: 'kept', label: 'Kept', color: '#16a34a' },
  { key: 'inPlay', label: 'In play', color: '#0ea5e9' },
  { key: 'closedForCost', label: 'Closed for cost', color: '#d97706' },
  { key: 'rollCost', label: 'Roll cost', color: '#7c3aed' },
];

// Sub-split of Kept by mechanism — sequential steps of the Kept green, not new hues.
const MECHANISMS: { key: KeptMechanism; label: string; color: string }[] = [
  { key: 'expired', label: 'Expired worthless', color: '#166534' },
  { key: 'assigned', label: 'Assigned / exercised', color: '#16a34a' },
  { key: 'boughtBack', label: 'Bought back cheap', color: '#4ade80' },
];

const fmt = (n: number) => `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const pct = (v: number, total: number) => (total > 0 ? `${Math.round((v / total) * 100)}%` : '0%');

type SNode = { id: string; label: string; color: string };
type SLink = { source: number; target: number; value: number; fate: PremiumFlowFate; mech?: KeptMechanism };

const WIDTH = 720;
const HEIGHT = 320;

export function PremiumFlowChart({ flow }: { flow: PremiumFlow }) {
  // Either a fate key or a kept-mechanism key; the two sets don't overlap.
  const [selected, setSelected] = useState<string | null>(null);
  const graph = useMemo(() => buildGraph(flow), [flow]);

  const toggle = (key: string) => setSelected((s) => (s === key ? null : key));
  const linkDimmed = (link: SLink) => {
    if (selected == null) return false;
    const mech = MECHANISMS.find((m) => m.key === selected);
    // Selecting a mechanism keeps its whole path lit: collected → Kept → mechanism.
    if (mech) return !(link.mech === mech.key || (link.fate === 'kept' && !link.mech));
    return link.fate !== selected;
  };

  return (
    <>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width="100%" role="img" aria-label="Premium flow Sankey">
        {graph.links.map((link) => {
          const color = link.mech
            ? MECHANISMS.find((m) => m.key === link.mech)!.color
            : FATES.find((f) => f.key === link.fate)!.color;
          const label = link.mech
            ? MECHANISMS.find((m) => m.key === link.mech)!.label
            : FATES.find((f) => f.key === link.fate)!.label;
          const share = link.mech ? `${pct(link.value, flow.kept)} of Kept` : pct(link.value, flow.collected);
          return (
            <path
              key={link.mech ?? link.fate}
              d={sankeyLinkHorizontal<SNode, SLink>()(link) ?? undefined}
              fill="none"
              stroke={color}
              strokeOpacity={linkDimmed(link) ? 0.12 : 0.4}
              strokeWidth={Math.max(1, (link.width ?? 1))}
              onClick={() => toggle(link.mech ?? link.fate)}
              style={{ cursor: 'pointer' }}
            >
              <title>{`${label}: ${fmt(link.value)} · ${share}`}</title>
            </path>
          );
        })}
        {graph.nodes.map((n) => {
          const fate = FATES.find((f) => f.key === n.id);
          const mech = MECHANISMS.find((m) => m.key === n.id);
          const clickable = fate != null || mech != null;
          return (
            <g
              key={n.id}
              onClick={() => clickable && toggle(n.id)}
              style={{ cursor: clickable ? 'pointer' : 'default' }}
            >
              {fate && (
                <title>{`${fate.label}: ${fmt(flow[fate.key])} · ${pct(flow[fate.key], flow.collected)}`}</title>
              )}
              {mech && (
                <title>{`${mech.label}: ${fmt(flow.keptBreakdown?.[mech.key] ?? 0)} · ${pct(flow.keptBreakdown?.[mech.key] ?? 0, flow.kept)} of Kept`}</title>
              )}
              {n.id === 'collected' && (
                <title>{`Premium Collected: ${fmt(flow.collected)} · 100%`}</title>
              )}
              <rect x={n.x0} y={n.y0} width={(n.x1 ?? 0) - (n.x0 ?? 0)} height={Math.max(1, (n.y1 ?? 0) - (n.y0 ?? 0))} fill={n.color} rx={2} />
              <text x={(n.x0 ?? 0) < WIDTH / 2 ? (n.x1 ?? 0) + 6 : (n.x0 ?? 0) - 6} y={((n.y0 ?? 0) + (n.y1 ?? 0)) / 2} dy="0.35em" textAnchor={(n.x0 ?? 0) < WIDTH / 2 ? 'start' : 'end'} fontSize={12} fill="#334155">{n.label}</text>
            </g>
          );
        })}
      </svg>

      <p className="text-xs text-gray-500 mt-2">
        Net realized P&L: <span className="font-medium text-gray-700">{fmt(flow.netRealizedPnl)}</span>
        {flow.lossBeyondPremium > 0 && <> · Loss beyond premium: <span className="font-medium text-red-600">{fmt(flow.lossBeyondPremium)}</span></>}
      </p>

      {selected && <DrillList flow={flow} selected={selected} />}
    </>
  );
}

function DrillList({ flow, selected }: { flow: PremiumFlow; selected: string }) {
  const mech = MECHANISMS.find((m) => m.key === selected);
  const rows = flow.contributors
    .filter((c) => (mech ? c.fate === 'kept' && c.keptBy === mech.key : c.fate === selected))
    .sort((a, b) => b.amount - a.amount);
  const label = mech?.label ?? FATES.find((f) => f.key === selected)?.label ?? selected;
  if (rows.length === 0) return null;
  return (
    <div className="mt-4 border-t border-gray-100 pt-3">
      <p className="text-xs font-medium text-gray-700 mb-2">{label} — contributing chains</p>
      <ul className="space-y-1">
        {rows.map((c, i) => (
          <li key={`${c.chainId}-${i}`} className="flex justify-between text-sm">
            <Link to={`/chains/${c.chainId}`} className="text-brand-700 hover:underline">{c.symbol}</Link>
            <span className="text-gray-600">{fmt(c.amount)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function buildGraph(flow: PremiumFlow): SankeyGraph<SNode, SLink> {
  const nodes: SNode[] = [
    { id: 'collected', label: 'Premium Collected', color: '#94a3b8' },
    ...FATES.map((f) => ({ id: f.key, label: f.label, color: f.color })),
  ];
  const links: SLink[] = FATES.map((f, i) => ({ source: 0, target: i + 1, value: Math.max(0, flow[f.key]), fate: f.key as PremiumFlowFate })).filter((l) => l.value > 0);

  // Second hop: Kept → mechanism nodes (only those with value, guard for
  // cached API responses that predate keptBreakdown).
  const keptIndex = 1 + FATES.findIndex((f) => f.key === 'kept');
  if (flow.keptBreakdown && flow.kept > 0) {
    for (const m of MECHANISMS) {
      const value = Math.max(0, flow.keptBreakdown[m.key] ?? 0);
      if (value <= 0) continue;
      nodes.push({ id: m.key, label: m.label, color: m.color });
      links.push({ source: keptIndex, target: nodes.length - 1, value, fate: 'kept', mech: m.key });
    }
  }

  const layout = sankey<SNode, SLink>().nodeWidth(16).nodePadding(14).extent([[1, 8], [WIDTH - 1, HEIGHT - 8]]);
  // d3-sankey mutates its input; spread copies so callers' nodes/links aren't mutated (all-zero graph is handled by the empty-state gate in the wrappers).
  return layout({ nodes: nodes.map((n) => ({ ...n })), links: links.map((l) => ({ ...l })) });
}
